import { AxiosRequestConfig } from 'axios';
import { mapToData } from 'src/common/http/mapToData';
import { mapToErrors } from 'src/common/http/mapToErrors';

import instance from '../../../common/http/axiosInstance';
import {
  ApproveAffiliateApplicationDto,
  GetAffiliateApplicationsParams,
  HoldAffiliateApplicationDto,
  PutFormConfigDTO,
  RejectAffiliateApplicationDto,
  UpdateAffiliateApplicationDto,
} from '../domain/affiliate-application-types';
import {
  AffiliateApplicationFormConfigResponse,
  AffiliateApplicationResponse,
  GetAffiliateApplicationsResponse,
} from './mappers';

const requests = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    instance.get(url, config).then(mapToData).catch(mapToErrors),
  post: async <T, K>(url: string, body?: T): Promise<K> => instance.post(url, body).then(mapToData).catch(mapToErrors),
  put: async <T, K>(url: string, body: T): Promise<K> => instance.put(url, body).then(mapToData).catch(mapToErrors),
  patch: async <T, K>(url: string, body: T): Promise<K> => instance.patch(url, body).then(mapToData).catch(mapToErrors),
};

export const affiliateApplicationService = {
  getApplications: async (
    projectId: string,
    params?: GetAffiliateApplicationsParams,
  ): Promise<GetAffiliateApplicationsResponse> =>
    requests.get<GetAffiliateApplicationsResponse>(`/projects/${projectId}/affiliate-applications`, {
      params,
    }),

  getApplication: async (projectId: string, applicationId: string): Promise<AffiliateApplicationResponse> =>
    requests.get<AffiliateApplicationResponse>(`/projects/${projectId}/affiliate-applications/${applicationId}`),

  approveApplication: async (
    projectId: string,
    applicationId: string,
    dto?: ApproveAffiliateApplicationDto,
  ): Promise<AffiliateApplicationResponse> =>
    requests.post<ApproveAffiliateApplicationDto, AffiliateApplicationResponse>(
      `/projects/${projectId}/affiliate-applications/${applicationId}/approve`,
      dto,
    ),

  rejectApplication: async (
    projectId: string,
    applicationId: string,
    dto: RejectAffiliateApplicationDto,
  ): Promise<AffiliateApplicationResponse> =>
    requests.post<RejectAffiliateApplicationDto, AffiliateApplicationResponse>(
      `/projects/${projectId}/affiliate-applications/${applicationId}/reject`,
      dto,
    ),

  holdApplication: async (
    projectId: string,
    applicationId: string,
    dto?: HoldAffiliateApplicationDto,
  ): Promise<AffiliateApplicationResponse> =>
    requests.post<HoldAffiliateApplicationDto, AffiliateApplicationResponse>(
      `/projects/${projectId}/affiliate-applications/${applicationId}/hold`,
      dto,
    ),

  updateApplication: async (
    projectId: string,
    applicationId: string,
    dto: UpdateAffiliateApplicationDto,
  ): Promise<AffiliateApplicationResponse> =>
    requests.post<UpdateAffiliateApplicationDto, AffiliateApplicationResponse>(
      `/projects/${projectId}/affiliate-applications/${applicationId}/update`,
      dto,
    ),

  getFormConfig: async (projectId: string): Promise<AffiliateApplicationFormConfigResponse> =>
    requests.get<AffiliateApplicationFormConfigResponse>(`/projects/${projectId}/affiliate-application-form-config`),

  putFormConfig: async (projectId: string, dto: PutFormConfigDTO): Promise<AffiliateApplicationFormConfigResponse> =>
    requests.put<PutFormConfigDTO, AffiliateApplicationFormConfigResponse>(
      `/projects/${projectId}/affiliate-application-form-config`,
      dto,
    ),

  enableFormConfig: async (projectId: string, enabled: boolean): Promise<AffiliateApplicationFormConfigResponse> =>
    requests.patch<{ enabled: boolean }, AffiliateApplicationFormConfigResponse>(
      `/projects/${projectId}/affiliate-application-form-config/enable`,
      { enabled },
    ),
};
